/**
 * InlineAssociationEditor - Inline association definitions for entity type creation
 *
 * Provides a repeating list of expandable association forms that can be added
 * to a new entity type without navigating to a separate panel.
 * Follows the same structural pattern as InlinePropertyEditor.tsx.
 */

import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Plus, Trash01 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { Select } from '@/lib/base/forms/select/select';
import type { CrmObjectTypeSchema } from '@/lib/crm/core/SchemaClient';
import { createSchemaClient } from '@/lib/crm/core/SchemaClient';

// =============================================================================
// Types
// =============================================================================

export interface AssociationDefinition {
  /** Unique client-side ID */
  id: string;
  /** Selected target entity type name (e.g. 'ahoy_contact', 'org_project') */
  targetEntityTypeName: string;
  /** Cardinality of the association */
  cardinality: 'one_to_one' | 'one_to_many' | 'many_to_one' | 'many_to_many';
  /** Forward label (e.g. 'Related Contacts') */
  displayName: string;
  /** Reverse label (e.g. 'Related Projects') */
  displayNameReverse: string;
}

export interface InlineAssociationEditorProps {
  /** Current list of association definitions */
  associations: AssociationDefinition[];
  /** Callback when associations change */
  onChange: (associations: AssociationDefinition[]) => void;
  /** Whether the editor is disabled (e.g. during save) */
  disabled?: boolean;
  /** Org ID for fetching entity types */
  orgId: number;
  /** The entity type name being created — used to filter self-associations */
  sourceEntityTypeName: string;
  /** Optional plural display name for auto-suggesting reverse label */
  sourcePluralName?: string;
}

// =============================================================================
// Helpers
// =============================================================================

/** Create a blank association definition. */
// eslint-disable-next-line react-refresh/only-export-components -- helper function co-located with InlineAssociationEditor component
export function createBlankAssociation(): AssociationDefinition {
  return {
    id: crypto.randomUUID(),
    targetEntityTypeName: '',
    cardinality: 'many_to_many',
    displayName: '',
    displayNameReverse: '',
  };
}

/**
 * System entity types that are allowed as association targets.
 * Custom entity types (org_ prefix) are always allowed.
 */
const ALLOWED_SYSTEM_TYPES = new Set([
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
  'ahoy_user',
  'ahoy_meeting',
]);

/** Filter entity types to valid association targets (whitelist approach). */
function filterTargetTypes(
  types: CrmObjectTypeSchema[],
  sourceEntityTypeName: string
): CrmObjectTypeSchema[] {
  return types.filter(t => {
    if (t.entity_type_name === sourceEntityTypeName) {
      return false;
    }
    // Exclude timeline identifiers (reserved for internal use)
    if (t.entity_type_name.endsWith('_timeline')) {
      return false;
    }
    // Allow custom entity types (excluding internal pipeline/stage support types) and whitelisted system types
    return (
      (t.entity_type_name.startsWith('org_') &&
        !t.entity_kind.includes('pipeline') &&
        !t.entity_kind.includes('stage')) ||
      ALLOWED_SYSTEM_TYPES.has(t.entity_type_name)
    );
  });
}

const CARDINALITY_LABELS: Record<AssociationDefinition['cardinality'], string> =
  {
    many_to_many: 'Many to Many',
    many_to_one: 'Many to One',
    one_to_many: 'One to Many',
    one_to_one: 'One to One',
  };

// =============================================================================
// AssociationCard - Single expandable association form
// =============================================================================

interface AssociationCardProps {
  association: AssociationDefinition;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onChange: (updated: AssociationDefinition) => void;
  onRemove: () => void;
  disabled: boolean;
  targetTypes: CrmObjectTypeSchema[];
  typesLoading: boolean;
  error?: string;
}

function AssociationCard({
  association,
  index,
  isExpanded,
  onToggle,
  onChange,
  onRemove,
  disabled,
  targetTypes,
  typesLoading,
  error,
}: AssociationCardProps) {
  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;
  const targetType = targetTypes.find(
    t => t.entity_type_name === association.targetEntityTypeName
  );
  const targetLabel = targetType?.label ?? association.targetEntityTypeName;
  const _cardinalityLabel = CARDINALITY_LABELS[association.cardinality];
  const displayLabel =
    association.displayName.trim() || `Association ${index + 1}`;

  const updateField = useCallback(
    <K extends keyof AssociationDefinition>(
      field: K,
      value: AssociationDefinition[K]
    ) => {
      onChange({ ...association, [field]: value });
    },
    [association, onChange]
  );

  const handleTargetChange = useCallback(
    (key: string | null) => {
      if (!key) {
        onChange({ ...association, targetEntityTypeName: '' });
        return;
      }
      const selected = targetTypes.find(t => t.entity_type_name === key);
      const updates: Partial<AssociationDefinition> = {
        targetEntityTypeName: key,
      };
      // Auto-suggest display name from target's plural label
      if (selected?.label_plural && !association.displayName.trim()) {
        updates.displayName = selected.label_plural;
      }
      onChange({ ...association, ...updates });
    },
    [association, onChange, targetTypes]
  );

  const targetTypeItems = useMemo(
    () => targetTypes.map(t => ({ id: t.entity_type_name, label: t.label })),
    [targetTypes]
  );

  const cardinalityItems = useMemo(
    () =>
      (
        Object.entries(CARDINALITY_LABELS) as [
          AssociationDefinition['cardinality'],
          string,
        ][]
      ).map(([id, label]) => ({ id, label })),
    []
  );

  return (
    <div className='border border-border-primary rounded-lg overflow-hidden'>
      {/* Header - always visible */}
      <button
        type='button'
        onClick={onToggle}
        disabled={disabled}
        className='w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-bg-secondary transition-colors'
      >
        <ChevronIcon className='w-4 h-4 text-text-tertiary flex-shrink-0' />
        <span className='text-sm font-medium text-text-primary truncate flex-1'>
          {displayLabel}
        </span>
        {targetLabel && (
          <span className='text-xs text-text-tertiary flex-shrink-0'>
            {targetLabel}
          </span>
        )}
      </button>

      {/* Expanded body */}
      {isExpanded && (
        <div className='px-3 pb-3 space-y-4 border-t border-border-primary pt-3'>
          {/* Error */}
          {error && <p className='text-sm text-text-error-primary'>{error}</p>}

          {/* Target Entity Type */}
          <Select.ComboBox
            label='Target object type'
            placeholder={typesLoading ? 'Loading…' : 'Select object type'}
            items={targetTypeItems}
            selectedKey={association.targetEntityTypeName || null}
            onSelectionChange={key =>
              handleTargetChange(
                key !== null && key !== undefined ? String(key) : null
              )
            }
            isRequired
            isDisabled={disabled || typesLoading}
            shortcut={false}
            aria-label='Target object type'
          >
            {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select.ComboBox>

          {/* Cardinality */}
          <Select
            label='Cardinality'
            placeholder='Select cardinality'
            items={cardinalityItems}
            selectedKey={association.cardinality}
            onSelectionChange={key =>
              updateField(
                'cardinality',
                key as AssociationDefinition['cardinality']
              )
            }
            isRequired
            isDisabled={disabled}
          >
            {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>

          {/* Display Name (forward) */}
          <Input
            label='Display name'
            placeholder='e.g. Related Contacts'
            value={association.displayName}
            onChange={val => updateField('displayName', val)}
            isDisabled={disabled}
            isRequired
          />

          {/* Display Name (reverse) */}
          <Input
            label='Display name (reverse)'
            placeholder='e.g. Related Projects'
            value={association.displayNameReverse}
            onChange={val => updateField('displayNameReverse', val)}
            isDisabled={disabled}
            isRequired
          />

          {/* Remove button */}
          <div className='pt-1'>
            <Button
              size='sm'
              color='secondary-destructive'
              iconLeading={Trash01}
              onClick={onRemove}
              isDisabled={disabled}
            >
              Remove association
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// InlineAssociationEditor - Main component
// =============================================================================

export function InlineAssociationEditor({
  associations,
  onChange,
  disabled = false,
  orgId,
  sourceEntityTypeName,
  sourcePluralName,
}: InlineAssociationEditorProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data: allTypes = [], isLoading: typesLoading } = useQuery({
    queryKey: ['objectTypes', orgId],
    queryFn: async () => {
      const client = createSchemaClient(orgId);
      return client.getObjectTypes();
    },
  });

  const targetTypes = useMemo(
    () => filterTargetTypes(allTypes, sourceEntityTypeName),
    [allTypes, sourceEntityTypeName]
  );

  // Validation errors
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const targetSet = new Set<string>();
    associations.forEach(assoc => {
      if (!assoc.targetEntityTypeName) {
        errors[assoc.id] = 'Target object type is required';
      } else if (targetSet.has(assoc.targetEntityTypeName)) {
        errors[assoc.id] = 'Duplicate target object type';
      }
      if (assoc.targetEntityTypeName) {
        targetSet.add(assoc.targetEntityTypeName);
      }

      if (!assoc.displayName.trim()) {
        errors[assoc.id] = errors[assoc.id] || 'Display name is required';
      }
      if (!assoc.displayNameReverse.trim()) {
        errors[assoc.id] =
          errors[assoc.id] || 'Reverse display name is required';
      }
    });
    return errors;
  }, [associations]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    const newAssoc = createBlankAssociation();
    // Auto-suggest reverse display name from source plural name
    if (sourcePluralName) {
      newAssoc.displayNameReverse = sourcePluralName;
    }
    onChange([...associations, newAssoc]);
    setExpandedIds(prev => new Set([...prev, newAssoc.id]));
  }, [associations, onChange, sourcePluralName]);

  const handleChange = useCallback(
    (index: number, updated: AssociationDefinition) => {
      const next = [...associations];
      next[index] = updated;
      onChange(next);
    },
    [associations, onChange]
  );

  const handleRemove = useCallback(
    (index: number) => {
      const removed = associations[index];
      onChange(associations.filter((_, i) => i !== index));
      setExpandedIds(prev => {
        const next = new Set(prev);
        next.delete(removed.id);
        return next;
      });
    },
    [associations, onChange]
  );

  return (
    <div>
      <label className='block text-sm font-medium text-text-primary mb-2'>
        Associations
      </label>

      {associations.length === 0 ? (
        <div className='py-3 px-3 text-center border border-dashed border-border-secondary rounded-lg mb-3'>
          <p className='text-sm text-text-tertiary'>
            No associations yet. Add associations to link this object type to
            others.
          </p>
        </div>
      ) : (
        <div className='space-y-2 mb-3'>
          {associations.map((assoc, index) => (
            <AssociationCard
              key={assoc.id}
              association={assoc}
              index={index}
              isExpanded={expandedIds.has(assoc.id)}
              onToggle={() => toggleExpanded(assoc.id)}
              onChange={updated => handleChange(index, updated)}
              onRemove={() => handleRemove(index)}
              disabled={disabled}
              targetTypes={targetTypes}
              typesLoading={typesLoading}
              error={validationErrors[assoc.id]}
            />
          ))}
        </div>
      )}

      <Button
        size='sm'
        color='secondary'
        iconLeading={Plus}
        onClick={handleAdd}
        isDisabled={disabled}
      >
        Add association
      </Button>
    </div>
  );
}
